/**
 * OUTGOING MESSAGE SOURCE — La mail di partenza di una mail in uscita (POLL-6): quella a cui
 * risponde, mostrata in sola lettura accanto alla risposta nel dialogo della Sorveglianza, così
 * chi la controlla vede insieme domanda e risposta.
 *
 * Fra i dati e il corpo della mail c'è lo slot `surveillance:source` di `Message`: il core non sa
 * che cosa sia una pratica, quindi il link alla pratica lo mette il plugin che la conosce
 * (Pollini). Prima del corpo e non dopo: sotto una mail lunga non si vedeva senza scorrere.
 *
 * Componente a sé e non dentro il dialogo apposta: se le due colonne affiancate non convincono, la
 * mail di partenza si sposta (sotto, in un pannello) senza toccare né il dialogo né lo slot.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@crm/shared';
import { SandboxedHtmlFrame } from '../../components/SandboxedHtmlFrame';
import { useSlotContributors } from '../../plugin/useSlotContributors';
import { extractError } from '../../store/thunkHelpers';
import { messageApi } from '../message/messageApi';
import type { Message } from '../message/messageTypes';

interface Props {
  /** Il `Message` ricevuto a cui la mail risponde (`replyToMessageId`). */
  messageId: number;
}

export function OutgoingMessageSource({ messageId }: Props) {
  const { t } = useTranslation();
  const contributors = useSlotContributors('Message', 'surveillance:source');
  const [message, setMessage] = useState<Message | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Una risposta arrivata dopo un cambio di mail si scarta: il dialogo è rimontato per riga
    // (key), ma la guardia costa niente e regge anche un uso diverso.
    let current = true;
    setMessage(null);
    setError(null);
    messageApi.getById(messageId).then(
      (loaded) => { if (current) setMessage(loaded); },
      (err) => { if (current) setError(extractError(err)); },
    );
    return () => { current = false; };
  }, [messageId]);

  return (
    <section className="outgoing-source" aria-label={t('outgoingMessage.source.title')}>
      <span className="tc-kicker outgoing-split__title">{t('outgoingMessage.source.title')}</span>

      {error && <p className="text-error">{t('outgoingMessage.source.loadError')}</p>}
      {!message && !error && <p className="text-muted">{t('common.loading')}</p>}

      {message && (
        <>
          <dl>
            <dt>{t('outgoingMessage.fields.from')}</dt>
            <dd>
              {message.senderName
                ? `${message.senderName} <${message.senderEmailAddress}>`
                : message.senderEmailAddress}
            </dd>
            <dt>{t('outgoingMessage.source.receiptDate')}</dt>
            <dd>{formatDateTime(message.receiptDate)}</dd>
            <dt>{t('outgoingMessage.fields.subject')}</dt>
            <dd>{message.subject || t('outgoingMessage.noSubject')}</dd>
          </dl>

          {contributors.length > 0 && (
            // Lo spazio dal corpo lo dà il core: il plugin non sa che cosa viene dopo.
            <div className="outgoing-source__slot">
              {contributors.map((c) => (
                <c.component key={c.key} entityId={message.messageId} />
              ))}
            </div>
          )}

          {message.bodyHtml ? (
            // HTML di una mail ricevuta, non fidato: nell'iframe isolato come nella Smart Inbox. Alto
            // quanto la mail, perché scorre già il dialogo.
            <SandboxedHtmlFrame className="message-body-html" title={t('message.bodyHtmlTitle')}
              html={message.bodyHtml} fitContent />
          ) : (
            <div className="message-body">{message.body}</div>
          )}
        </>
      )}
    </section>
  );
}
